import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const CREDIT_LABELS = [
  ["article_brief", "Article brief"],
  ["article_draft_standard", "Article (standard ~1,800w)"],
  ["article_draft_long", "Article (long ~3,500w)"],
  ["article_regenerate_full", "Full article regeneration"],
  ["article_edit_section", "Section edit"],
  ["growth_plan", "30/60/90 growth plan"],
  ["ai_visibility_check", "AI visibility check"],
  ["seo_audit_full", "SEO audit (full)"],
  ["report_narrative", "Report narrative"],
  ["keyword_batch", "Keyword batch (10 kw)"],
];

const LIMIT_MESSAGE = "You've reached your included usage limit. Upgrade your plan, purchase prepaid credits, or wait until your next billing cycle.";

export default function UsagePage() {
  const [snap, setSnap] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [buying, setBuying] = useState(null);

  const refresh = () => {
    api.get("/usage/mine").then(({ data }) => setSnap(data));
    api.get("/usage/ledger?limit=25").then(({ data }) => setLedger(data));
  };
  useEffect(() => { refresh(); }, []);

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const { data } = await api.post("/billing/credit-pack", { pack_id: packId, origin_url: window.location.origin });
      if (data?.checkout_url) window.location.assign(data.checkout_url);
      else toast.error("Could not start checkout. Please try again.");
    } catch {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setBuying(null);
    }
  };

  if (!snap) return <DashboardLayout><p className="text-muted-foreground">Loading usage…</p></DashboardLayout>;

  const pct = Math.min(100, snap.percent_used || 0);
  const level = pct >= 100 ? 100 : pct >= 85 ? 85 : pct >= 70 ? 70 : 0;
  const barColor = level === 100 ? "bg-red-500" : level === 85 ? "bg-amber-500" : level === 70 ? "bg-gold-500" : "bg-navy-900";
  const resetLabel = snap.next_reset_at ? new Date(snap.next_reset_at).toLocaleDateString() : "start of next billing cycle";
  const creditCosts = snap.credit_costs || {};

  return (
    <DashboardLayout>
      <PageHeader
        overline="Usage & credits"
        title="Prepaid usage. No surprise bills."
        description={`${snap.plan_name || snap.plan_id} plan · included credits reset on ${resetLabel}. Everything is prepaid — when you reach your included usage, upgrade or add prepaid credits.`}
      />

      {!snap.usage_allowed && snap.entitlement_reason === "no_subscription" && (
        <div className="mb-4 border border-navy-900/20 bg-ivory-100 rounded-sm px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="choose-plan-banner">
          <span className="text-navy-900 font-medium">Choose a plan to unlock AI-powered actions.</span>
          <Link to="/pricing" className="text-xs px-3 py-1.5 bg-navy-900 text-ivory-50 rounded-sm hover:bg-navy-800" data-testid="choose-plan-cta">
            View plans
          </Link>
        </div>
      )}
      {!snap.usage_allowed && snap.entitlement_reason !== "no_subscription" && (
        <div className="mb-4 border border-red-500/40 text-red-800 bg-red-50 rounded-sm px-4 py-3 text-sm" data-testid="billing-status-blocked">
          {snap.entitlement_message || "Your subscription payment needs attention. AI, crawl, export and sync operations are paused until billing is updated."}{" "}
          <Link to="/pricing" className="underline">Manage billing</Link>
        </div>
      )}

      {/* Included monthly credits */}
      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-6" data-testid="usage-meter">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="editorial-overline">Included monthly credits</p>
              <p className="font-serif text-3xl mt-1" data-testid="usage-used">
                {snap.used_credits.toLocaleString()} <span className="text-muted-foreground text-lg">/ {snap.included_credits.toLocaleString()} used</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Included remaining</p>
              <p className="font-serif text-2xl mt-1" data-testid="usage-included-remaining">{Math.max(0, snap.included_remaining ?? 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="h-2 bg-ivory-100 rounded-sm overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} data-testid="usage-bar" />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
            <span>{pct.toFixed(1)}% of included credits used</span>
            <span data-testid="usage-next-reset">Resets {resetLabel}</span>
          </div>
          {level === 70 && (
            <div className="mt-4 border border-gold-500/40 text-navy-900 bg-gold-500/10 rounded-sm px-4 py-2 text-sm" data-testid="usage-warn-70">
              You&rsquo;ve used {pct.toFixed(0)}% of this cycle&rsquo;s included credits.
            </div>
          )}
          {level === 85 && (
            <div className="mt-4 border border-amber-500/40 text-amber-800 bg-amber-50 rounded-sm px-4 py-2 text-sm" data-testid="usage-warn-85">
              You&rsquo;ve used {pct.toFixed(0)}% of this cycle&rsquo;s included credits. To avoid interruption, <Link to="/pricing" className="underline">upgrade your plan</Link> or add a prepaid credit pack below.
            </div>
          )}
          {level === 100 && (
            <div className="mt-4 border border-red-500/40 text-red-800 bg-red-50 rounded-sm px-4 py-2 text-sm" data-testid="usage-warn-100">
              {LIMIT_MESSAGE}{snap.purchased_remaining > 0 ? " Your prepaid credit-pack balance is being used now." : ""}
            </div>
          )}
        </div>

        {/* Prepaid pack credits */}
        <div className="bg-white border border-border rounded-sm p-6" data-testid="prepaid-balance">
          <p className="editorial-overline">Prepaid credit-pack credits</p>
          <p className="font-serif text-3xl mt-1" data-testid="usage-purchased-remaining">{Math.max(0, snap.purchased_remaining ?? 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">remaining · used only after included credits</p>
          <p className="text-xs text-muted-foreground mt-3">Total available now: <strong data-testid="usage-remaining">{Math.max(0, snap.remaining_credits).toLocaleString()}</strong> credits</p>
          {(level >= 85 || snap.remaining_credits < 60) && (
            <Link to="/pricing" className="mt-4 inline-block text-xs px-3 py-1.5 bg-navy-900 text-ivory-50 rounded-sm hover:bg-navy-800" data-testid="usage-upgrade-cta">
              Upgrade plan
            </Link>
          )}
        </div>
      </section>

      {/* Counters */}
      <section className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="usage-counters">
        {[
          ["articles", "Articles", snap.limits.articles_per_month],
          ["regenerations", "Regenerations", snap.limits.regenerations_per_month],
          ["ai_visibility_checks", "AI visibility checks", snap.limits.ai_visibility_checks_per_month],
          ["audits", "SEO audits", snap.limits.audits_per_month],
          ["pages_scanned", "Crawl pages", snap.limits.pages_scanned_per_month],
          ["reports", "Reports", snap.limits.reports_per_month],
        ].map(([k, label, cap]) => {
          const used = snap.counters[k] || 0;
          const p = Math.min(100, (used / Math.max(1, cap)) * 100);
          return (
            <div key={k} className="bg-white border border-border rounded-sm p-4" data-testid={`counter-${k}`}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className="font-serif text-2xl mt-1">{used}</p>
              <p className="text-xs text-muted-foreground">of {cap.toLocaleString()} / mo</p>
              <div className="mt-2 h-1 bg-ivory-100 rounded-sm overflow-hidden">
                <div className={`h-full ${p >= 100 ? "bg-red-500" : p >= 85 ? "bg-amber-500" : p >= 70 ? "bg-gold-500" : "bg-navy-900"}`} style={{ width: `${p}%` }} />
              </div>
            </div>
          );
        })}
      </section>

      {/* Credit cost table */}
      <section className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-6" data-testid="credit-costs-table">
          <p className="editorial-overline">Credit cost per AI action</p>
          <table className="w-full mt-3 text-sm">
            <tbody>
              {CREDIT_LABELS.map(([op, label]) => (
                <tr key={op} className="border-b border-border last:border-0">
                  <td className="py-2.5 pr-3 text-navy-900">{label}</td>
                  <td className="py-2.5 text-right font-mono text-gold-600">{creditCosts[op] ?? "—"} credits</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3">Cheap models are used for short summaries, metadata, and keyword classification. Standard models are used for briefs, outlines, and articles. Longer articles and full regenerations use higher-capability models.</p>
        </div>

        <div className="bg-white border border-border rounded-sm p-6" data-testid="credit-packs">
          <p className="editorial-overline">Add prepaid credits</p>
          <p className="text-sm text-muted-foreground mt-2 mb-4">Paid up front, valid 12 months, used only after your included credits. Never billed after the fact.</p>
          {snap.overage_packs.map((pack) => (
            <button
              key={pack.id}
              type="button"
              onClick={() => buyPack(pack.id)}
              disabled={buying === pack.id}
              data-testid={`buy-pack-${pack.id}`}
              className="w-full mb-2 flex items-center justify-between px-4 py-3 border border-border hover:border-gold-500 rounded-sm text-sm disabled:opacity-50"
            >
              <span>+{pack.credits.toLocaleString()} credits</span>
              <span className="font-serif text-lg">${pack.price_usd.toFixed(2)}</span>
            </button>
          ))}
          <p className="mt-3 text-[11px] text-muted-foreground">
            No automatic purchases, no overage invoices. When both balances reach zero, AI actions pause until you upgrade, add credits, or your cycle resets.
          </p>
        </div>
      </section>

      {/* Ledger */}
      <section className="mt-6 bg-white border border-border rounded-sm p-6" data-testid="usage-ledger">
        <p className="editorial-overline">Recent AI operations</p>
        {ledger.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-3">No AI operations yet this period. Generate an article or run an AI visibility check to get started.</p>
        ) : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="text-left py-2">Time</th>
                  <th className="text-left py-2">Operation</th>
                  <th className="text-left py-2">Model</th>
                  <th className="text-right py-2">Credits</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row, i) => (
                  <tr key={`${row.created_at}-${i}`} className="border-b border-border last:border-0" data-testid="ledger-row">
                    <td className="py-2 text-xs text-muted-foreground">{new Date(row.created_at).toLocaleString()}</td>
                    <td className="py-2">{row.operation}</td>
                    <td className="py-2 text-xs text-muted-foreground">{row.model || "—"}</td>
                    <td className="py-2 text-right font-mono">{row.credits_charged ?? row.credits}</td>
                    <td className="py-2 text-right">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${row.status === "success" ? "border-emerald-500/40 text-emerald-600" : "border-red-500/40 text-red-600"}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
